import { inject, Injectable, OnDestroy } from '@angular/core';
import { Provider } from '@app/core/services/api/API.service';
import { BehaviorSubject } from 'rxjs';
import { sortJSON } from '../../util.service';
import {
    MassiveChangeLayoutsService,
    ProviderService
} from '@app/core/api/services';
import { environment } from '@env/environment';

@Injectable({
    providedIn: 'root'
})
export class ProvidersService implements OnDestroy {
    private _providers$ = new BehaviorSubject<Provider[]>([]);
    private _loading$ = new BehaviorSubject<boolean>(true);
    private providers: Provider[] = [];
    private subscriptions: any[] = [];
    private _inFlight: Promise<void> | null = null;
    private _providerService = inject(ProviderService);
    private _massiveChangeLayoutsService = inject(MassiveChangeLayoutsService);

    constructor() {}

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    get providers$() {
        return this._providers$.asObservable();
    }

    get loading$() {
        return this._loading$.asObservable();
    }

    async listProviders(forceRefresh = false) {
        const cached = this._providers$.getValue();
        if (!forceRefresh && cached?.length > 0) {
            return;
        }

        if (this._inFlight && !forceRefresh) {
            return this._inFlight;
        }

        this._inFlight = this.loadProviders().finally(() => {
            this._inFlight = null;
        });

        return this._inFlight;
    }

    async findProviderById(id: string): Promise<Provider | undefined> {
        try {
            await this.listProviders();
            const providers = this._providers$.getValue() ?? [];
            return providers.find(provider => provider.id === id);
        } catch (error) {
            console.error('Error finding provider:', error);
            return undefined;
        }
    }

    upsertProvider(provider: Provider): void {
        if (!provider?.id) {
            return;
        }

        const current = this._providers$.getValue() ?? [];
        const index = current.findIndex(item => item.id === provider.id);
        const next =
            index >= 0
                ? current.map(item =>
                      item.id === provider.id ? { ...item, ...provider } : item
                  )
                : [...current, provider];

        this.providers = sortJSON(next, 'comertial_name', 'asc') as Provider[];
        this._providers$.next(this.providers);
    }

    private async loadProviders(): Promise<void> {
        try {
            this._loading$.next(true);
            this.providers = await this.listProvidersBD();
        } catch (err) {
            console.error('Error loading providers', err);
            this.providers = [];
        } finally {
            this._providers$.next(this.providers);
            this._loading$.next(false);
        }
    }

    private async listProvidersBD(): Promise<Provider[]> {
        const result = await this._providerService.listProviders();
        if (result?.length) {
            return sortJSON(result, 'comertial_name', 'asc') as Provider[];
        }

        const recovered = await this.recoverProvidersFromHistory();
        return sortJSON(recovered, 'comertial_name', 'asc') as Provider[];
    }

    /** Recupera proveedores usados en cargas masivas previas si el listado general falla. */
    private async recoverProvidersFromHistory(): Promise<Provider[]> {
        const providerIds = new Set<string>();

        if (environment.sourceDataProviderId) {
            providerIds.add(environment.sourceDataProviderId);
        }

        try {
            const layouts = await this._massiveChangeLayoutsService.listMassiveChangeLayouts();
            (layouts ?? [])
                .filter(
                    layout => layout?.type === 'premiums' && layout.providerId
                )
                .forEach(layout => providerIds.add(layout.providerId));
        } catch (error) {
            console.error('Error recovering provider ids from layouts:', error);
        }

        const recovered = await this._providerService.getProvidersByIds([
            ...providerIds
        ]);

        if (recovered.length > 0) {
            console.warn(
                `Se recuperaron ${recovered.length} proveedor(es) desde historial de cargas.`
            );
        }

        return recovered;
    }
}
